import { FC, PropsWithChildren, useRef, useState } from 'react';
import { ItemWrapperProps } from '@v2/pages/machines/common/types/props';
import { useHover } from 'react-native-web-hooks';
import { injectConditionalObject, isPhone } from '@helpers';
import { Element, scale } from '@components/frame';
import ColorWrapper, { checkColor } from '@styles/colors';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import React from 'react';
import Svg from '@styles/icons/svg';
import { Machine } from '@interfaces/machines';
import { ItemImg } from '@v2/pages/machines/common/components/Item/ItemImg';
import { ItemName } from '@v2/pages/machines/common/components/Item/ItemName';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

interface Props extends ItemWrapperProps, PropsWithChildren {
  shouldUseMachineImage: boolean;
}

const ItemWrapper: FC<Props> = ({
  item,
  setSelectedMachine,
  selected: s,
  C,
  nextSelected,
  generateImageURL,
  loading,
  displayTime,
  roundTop,
  roundBottom,
  children,
  shouldUseMachineImage,
}) => {
  const ref = useRef<View | null>(null);
  const [pressed, setPressed] = useState<boolean>(false);
  const hovered = useHover(ref);

  const pressItem = (_item: Machine) => {
    trackEvent(TRACKING_EVENTS.MACHINE_LIVE, {
      action: 'machineCardTapped',
      machineId: String(_item.machineId || ''),
      machineName: _item.shortName || _item.fullName || '',
      machineProductivityStatus: _item.machineStatus?.status || 'Not Productive',
      machineSubStatus: _item.machineStatus?.subStatus,
      machineExecutionMode: _item.machineStatus?.executionMode,
    });
    setSelectedMachine(_item);
  };

  const selected = s && !isPhone;
  const displayOverlay = selected || pressed || hovered;

  const pressHoverColor = (c: string) => {
    if (pressed) {
      return checkColor(c, 'pressed');
    }
    if (hovered) {
      return checkColor(c, 'hover');
    }
    return c;
  };

  const machineImageData = {
    imageData: shouldUseMachineImage
      ? {
          isCropped: item?.machineImage?.isCropped,
          extensionDerivative: item?.machineImage?.extensionDerivative,
          pageNumber: item?.machineImage?.imagePageDetails?.[item?.machineImage?.pageNumber - 1]?.pageNumber,
          rotationAngle: item?.machineImage?.imagePageDetails?.[item?.machineImage?.pageNumber - 1]?.rotationAngle,
        }
      : item.machineStatus.imageData,
    imageDetails: shouldUseMachineImage ? item?.machineImage?.imagePageDetails : item.machineStatus.imageDetails,
    imageHash: shouldUseMachineImage ? item?.machineImage?.contentHash : item.machineStatus.imageHash,
    imageRandomHash: item.machineStatus.imageRandomHash,
    ...injectConditionalObject(shouldUseMachineImage, { documentId: item?.machineImage?.documentId }),
  };

  return (
    <TouchableOpacity
      style={[
        {
          paddingLeft: scale(isPhone ? 0 : displayOverlay ? 12 : 22),
          paddingRight: scale(isPhone ? 0 : displayOverlay ? 12 : 22),
          width: '100%',
          height: scale(80),
          position: 'relative',
          zIndex: 100000,
        },
        roundTop && {
          borderTopLeftRadius: scale(10),
          borderTopRightRadius: scale(10),
        },
        roundBottom && {
          borderBottomLeftRadius: scale(10),
          borderBottomRightRadius: scale(10),
        },
      ]}
      onPress={() => {
        pressItem(item);
      }}
      activeOpacity={1}
      ref={ref}
      onPressIn={() => {
        setPressed(true);
      }}
      onPressOut={() => {
        setPressed(false);
      }}
    >
      <View
        style={[
          {
            width: '100%',
            height: '100%',
            borderRadius: scale(18),
            paddingTop: scale(10),
            flexDirection: 'row',
            overflow: 'hidden',
          },
          selected && {
            backgroundColor: C('Selected/Light/List item'),
            borderBottomWidth: 0,
            borderRadius: scale(18),
            paddingLeft: scale(10),
            paddingRight: scale(10),
          },
          (pressed || hovered) &&
            !selected && {
              backgroundColor: C(pressHoverColor('Background/Light/Field')),
              borderRadius: scale(18),
              paddingLeft: scale(isPhone ? 0 : 10),
              paddingRight: scale(isPhone ? 0 : 10),
            },
          isPhone && { paddingLeft: scale(18) },
        ]}
      >
        <ItemImg
          {...{
            C,
            ...machineImageData,
            subStatus: item.machineStatus.subStatus,
            shortName: item.shortName,
            ncFile: item.machineStatus.ncFile,
            ncFileId: item.machineStatus.ncFileId,
            ncFileStatus: item.machineStatus?.ncFileStatus,
            partId: item.machineStatus?.partDetails?.partId,
            partName: item.machineStatus?.partDetails?.partName,
            isProcessing: item.isProcessing,
            machineId: item.id,
            generateImageURL,
            selected,
            loading,
          }}
        />
        <View
          style={{
            height: '100%',
            marginLeft: scale(5.5),
            flex: 1,
            borderBottomWidth: (selected || nextSelected) && !isPhone ? 0 : StyleSheet.hairlineWidth,
            borderBottomColor: !roundBottom ? C('Hairline/Light/No trans') : 'transparent',
            paddingBottom: scale(10),
          }}
        >
          <ItemName
            {...{
              isOperated: item.machineStatus.isOperated,
              status: item.machineStatus.status,
              shortName: item.shortName,
              isProcessingPI: item.isProcessingPI,
              hasNoDataInPeriod: item.hasNoDataInPeriod,
              C,
              selected,
              loading,
              displayTime,
            }}
          />
          {children}
          {isPhone && (
            <Element
              {...{
                config: {
                  top: 3,
                  right: 18,
                },
              }}
            >
              <Svg icon="chevronRightLight" color={'Label/Light/Quinary'} width={scale(7)} height={scale(13)} />
            </Element>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const compareProps = (prev: Props, next: Props) => {
  return (
    prev.item.machineStatus.status === next.item.machineStatus.status &&
    prev.item.machineStatus.subStatus === next.item.machineStatus.subStatus &&
    prev.item.machineStatus.lastUpdatedOn === next.item.machineStatus.lastUpdatedOn &&
    prev.item.machineStatus.ncFile === next.item.machineStatus.ncFile &&
    prev.item.machineStatus.imageRandomHash === next.item.machineStatus.imageRandomHash &&
    prev.item.machineStatus.imageHash === next.item.machineStatus.imageHash &&
    prev.item.shortName === next.item.shortName &&
    prev.item.isProcessing === next.item.isProcessing &&
    prev.item.isProcessingPI === next.item.isProcessingPI &&
    prev.C.scheme === next.C.scheme &&
    prev.selected === next.selected &&
    prev.nextSelected === next.nextSelected &&
    prev.displayTime === next.displayTime &&
    prev.loading === next.loading &&
    prev.forceRenderWhenDataUpdated === next.forceRenderWhenDataUpdated &&
    prev.shouldUseMachineImage === next.shouldUseMachineImage
  );
};

export default ColorWrapper(React.memo(ItemWrapper, compareProps));
